import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaEdit, 
  FaTrash, 
  FaInbox, 
  FaSortAlphaDown, 
  FaSortAlphaUp, 
  FaSort,
  FaBox,
  FaRuler,
  FaTruck
} from 'react-icons/fa';
import { getProducts, deleteProduct } from './productService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import Pagination from '../../components/shared/Pagination';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterGroup,
  FilterLabel,
  FilterButton,
  FilterSection
} from '../../components/styled/GridComponents';
import styled from 'styled-components';

const ProductName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  svg {
    color: ${props => props.theme.colors.primary};
    opacity: 0.8;
  }
`;

const ProductDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  margin: ${props => props.theme.spacing(1)} 0;
  line-height: 1.5;
`;

const UnitBadge = styled(StatusBadge)`
  background: ${props => props.theme.colors.primary}22;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  svg {
    font-size: 0.875em;
  }
`;

const SupplierInfo = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${props => props.theme.spacing(0)};
`;

const SupplierBadge = styled(StatusBadge)`
  background: ${props => props.theme.colors.secondary}22;
  color: ${props => props.theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};

  svg {
    font-size: 0.875em;
  }
`;

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getProducts();
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async () => {
    try {
      await deleteProduct(productToDelete);
      setProducts(products.filter(product => product.id !== productToDelete));
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleteModalOpen(false);
      setProductToDelete(null);
    }
  };

  const getFilteredProducts = () => {
    let filteredData = [...products];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(product => 
        product.name.toLowerCase().includes(query) ||
        product.description.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    return filteredData.sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      switch (sortConfig.field) {
        case 'name':
          return direction * a.name.localeCompare(b.name);
        case 'unit':
          return direction * a.unit.localeCompare(b.unit);
        default:
          return 0;
      }
    });
  };

  const currentProducts = getFilteredProducts().slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  console.log(currentProducts);

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Product Management"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/suppliers/products/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton 
            isActive={sortConfig.field === 'name'}
            onClick={() => handleSort('name')}
          >
            {sortConfig.field === 'name' ? 
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
              : <FaSort />
            }
            Name
          </FilterButton>
          <FilterButton 
            isActive={sortConfig.field === 'unit'}
            onClick={() => handleSort('unit')}
          >
            {sortConfig.field === 'unit' ? 
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
              : <FaSort />
            }
            Unit
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {loading ? (
          [...Array(6)].map((_, index) => (
            <Card key={index} isLoading>
              <CardContent />
            </Card>
          ))
        ) : currentProducts.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No products found. Try adjusting your search or add a new product.</p>
          </EmptyState>
        ) : (
          currentProducts.map((product) => (
            <Card key={product.id}>
              <CardHeader>
                <ProductName>
                  <FaBox />
                  {product.name}
                </ProductName>
                <UnitBadge>
                  <FaRuler />
                  {product.unit}
                </UnitBadge>
              </CardHeader>

              <CardContent>
                <ProductDescription>
                  {product.description || 'No description available'}
                </ProductDescription>
                <SupplierInfo>
                  {product.suppliers && product.suppliers.length > 0 ? (
                    <>
                      <SupplierBadge>
                        <FaTruck />
                        {product.suppliers.length} {product.suppliers.length === 1 ? 'Supplier' : 'Suppliers'}
                      </SupplierBadge>
                    </>
                  ) : (
                    <SupplierBadge>
                      <FaTruck />
                      No suppliers
                    </SupplierBadge>
                  )}
              </SupplierInfo>
              </CardContent>

              <ActionIconsContainer>
                <ActionButton 
                  onClick={() => navigate(`/suppliers/products/edit/${product.id}`)}
                  title="Edit product"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton 
                  onClick={() => {
                    setProductToDelete(product.id);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete product"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))
        )}
      </GridContainer>

      {!loading && getFilteredProducts().length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={getFilteredProducts().length}
          paginate={setCurrentPage}
          currentPage={currentPage}
        />
      )}

      {deleteModalOpen && (
        <Modal
          title="Delete Product"
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setProductToDelete(null);
          }}
          isOpen={deleteModalOpen}
        />
      )}
    </DashboardLayout>
  );
};

export default ProductList;